import React, { useEffect, useMemo, useState } from "react";
import "../styles/asset-management.css";
import { getAssetTasks, deleteAssetTask } from "../services/assetTaskService";
import { toast } from "../utils/toast";
import { getActionDropdownDirection } from "../utils/dropdownPosition";
import Modal from "../components/ui/Modal";
import ConfirmModal from "../components/ui/ConfirmModal";
import { useAppContext } from "../context/AppContext";
import { resolveRoleName } from "../utils/auth";

const AssetTaskManagementPage = () => {
  const { setActiveSection, setAssetTaskId } = useAppContext();
  const [roleChecked, setRoleChecked] = useState(false);
  const [canAccess, setCanAccess] = useState(false);
  const [assetTasks, setAssetTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [modal, setModal] = useState({
    open: false,
    title: "Notice",
    message: "",
  });
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    id: null,
    description: "",
    isBulk: false,
    count: 0,
  });
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState("down");

  const closeModal = () => setModal((m) => ({ ...m, open: false }));

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest(".actions-menu-wrapper")) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close dropdown on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") setActiveDropdown(null);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let user = null;
        try {
          const raw = sessionStorage.getItem("auth");
          const auth = raw ? JSON.parse(raw) : null;
          user = auth?.user || null;
        } catch {}
        const rn = user ? await resolveRoleName(user) : null;
        const norm = String(rn || "").toUpperCase();
        const allowed = norm === "SUPER_ADMIN" || norm === "SUPERADMIN";
        if (!cancelled) {
          setCanAccess(allowed);
          setRoleChecked(true);
        }
        if (!allowed) setActiveSection("dashboard");
      } catch {
        if (!cancelled) {
          setCanAccess(false);
          setRoleChecked(true);
        }
        setActiveSection("dashboard");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setActiveSection]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const assetTasksData = await getAssetTasks();
        setAssetTasks(Array.isArray(assetTasksData) ? assetTasksData : []);
      } catch (e) {
        toast.error(e?.message || "Failed to load asset tasks");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const allSelected = useMemo(
    () => assetTasks.length > 0 && selectedIds.size === assetTasks.length,
    [assetTasks, selectedIds],
  );

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(assetTasks.map((at) => at.asset_task_id)));
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const handleView = (assetTask) => {
    try {
      localStorage.setItem(
        "activeAssetTaskId",
        String(assetTask.asset_task_id),
      );
    } catch (_) {}
    setAssetTaskId(assetTask.asset_task_id);
    setActiveSection("asset-task-detail", {
      assetTaskId: assetTask.asset_task_id,
    });
  };

  const handleEdit = (assetTask) => {
    try {
      localStorage.setItem(
        "activeAssetTaskId",
        String(assetTask.asset_task_id),
      );
    } catch (_) {}
    setAssetTaskId(assetTask.asset_task_id);
    setActiveSection("asset-task-edit", {
      assetTaskId: assetTask.asset_task_id,
    });
  };

  const removeSingle = async (assetTask) => {
    const description = assetTask.description || "this asset task";
    setConfirmDelete({
      open: true,
      id: assetTask.asset_task_id,
      description,
      isBulk: false,
      count: 0,
    });
  };

  const confirmRemoveSingle = async () => {
    const { id } = confirmDelete;
    setConfirmDelete({
      open: false,
      id: null,
      description: "",
      isBulk: false,
      count: 0,
    });
    if (id == null) return;

    try {
      await deleteAssetTask(id);
      setAssetTasks((prev) => prev.filter((at) => at.asset_task_id !== id));
      toast.success("Asset task deleted");
      clearSelection();
    } catch (e) {
      toast.error(e?.message || "Failed to delete asset task");
    }
  };

  const bulkDelete = () => {
    if (selectedIds.size === 0) return;
    setConfirmDelete({
      open: true,
      id: null,
      description: "",
      isBulk: true,
      count: selectedIds.size,
    });
  };

  const confirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    setConfirmDelete({
      open: false,
      id: null,
      description: "",
      isBulk: false,
      count: 0,
    });
    if (ids.length === 0) return;
    try {
      // Delete sequentially to keep API load reasonable and handle errors clearly
      for (const id of ids) {
        try {
          await deleteAssetTask(id);
          setAssetTasks((prev) => prev.filter((at) => at.asset_task_id !== id));
        } catch (e) {
          // Surface but continue
          toast.error(e?.message || `Failed to delete asset task #${id}`);
        }
      }
      setSelectedIds(new Set());
      toast.success("Selected asset tasks deleted");
    } catch (e) {
      toast.error(e?.message || "Failed to delete selected asset tasks");
    }
  };

  const filteredAssetTasks = useMemo(() => {
    if (!searchTerm.trim()) return assetTasks;
    const lower = searchTerm.toLowerCase();
    return assetTasks.filter((at) => {
      const desc = String(at.description || "").toLowerCase();
      const type = String(at.type || "").toLowerCase();
      const taskId = String(at.task_id || "");
      const processId = String(at.process_id || "");
      return (
        desc.includes(lower) ||
        type.includes(lower) ||
        taskId.includes(lower) ||
        processId.includes(lower)
      );
    });
  }, [assetTasks, searchTerm]);

  if (!roleChecked) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Loading...</div>
        </div>
      </div>
    );
  }
  if (!canAccess) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Access denied</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-container">
        <div className="page-header">
          <div className="header-content">
            <div>
              <h2 className="page-title">Asset Task Management</h2>
              <p className="page-subtitle">
                Manage asset tasks for processes and workflows
              </p>
            </div>
            <div className="roles-toolbar">
              <div className="asset-search">
                <input
                  className="search-input"
                  type="text"
                  placeholder="Search by description, task, process"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      /* client filter only */
                    }
                  }}
                />
                <button
                  className="secondary-btn sm"
                  onClick={() => setSearchTerm("")}
                  disabled={loading}
                >
                  Clear
                </button>
              </div>
              <button
                className="primary-btn"
                onClick={() => {
                  setAssetTaskId(null);
                  setActiveSection("asset-task-create");
                }}
              >
                + Create Asset Task
              </button>
            </div>
          </div>
        </div>

        <div className="page-content">
          {selectedIds.size > 0 && (
            <div
              style={{
                marginBottom: "1rem",
                padding: "1rem",
                background: "var(--accent-tint-50)",
                borderRadius: "8px",
                border: "1px solid var(--accent-tint-100)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "0.75rem",
                }}
              >
                <span style={{ fontWeight: "600", color: "var(--accent-ink-800)" }}>
                  {selectedIds.size} {selectedIds.size === 1 ? "item" : "items"}{" "}
                  selected
                </span>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <button className="danger-btn sm" onClick={bulkDelete}>
                    Delete Selected
                  </button>
                  <button className="secondary-btn sm" onClick={clearSelection}>
                    Clear Selection
                  </button>
                </div>
              </div>
            </div>
          )}
          {loading ?
            <div className="no-results">Loading asset tasks...</div>
          : <>
              <div className="asset-table">
                <div className="asset-table-header">
                  <div className="cell checkbox">
                    <input
                      type="checkbox"
                      checked={
                        assetTasks.length > 0 &&
                        selectedIds.size === assetTasks.length
                      }
                      onChange={toggleSelectAll}
                    />
                  </div>
                  <div className="cell description">Description</div>
                  <div className="cell">Task</div>
                  <div className="cell">Process</div>
                  <div className="cell">Type</div>
                  <div className="cell">Position</div>
                  <div className="cell actions" style={{ textAlign: "center", justifyContent: "center" }}>Actions</div>
                </div>

                {filteredAssetTasks.length === 0 ?
                  <div className="no-results">
                    {searchTerm ?
                      "No matching asset tasks found"
                    : "No asset tasks yet"}
                  </div>
                : filteredAssetTasks.map((assetTask) => (
                    <div
                      className={`asset-table-row ${
                        selectedIds.has(assetTask.asset_task_id) ? "selected"
                        : ""
                      }`}
                      key={assetTask.asset_task_id}
                    >
                      <div className="cell checkbox">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(assetTask.asset_task_id)}
                          onChange={() => toggleSelect(assetTask.asset_task_id)}
                        />
                      </div>

                      <div className="cell description">
                        <div style={{ whiteSpace: "pre-wrap" }}>
                          {assetTask.description || "-"}
                        </div>
                      </div>

                      <div className="cell">
                        {assetTask.task && assetTask.task.task_name ?
                          assetTask.task.task_name
                        : `Task #${assetTask.task_id}`}
                      </div>

                      <div className="cell">
                        {assetTask.process && assetTask.process.process_name ?
                          assetTask.process.process_name
                        : assetTask.process_id ?
                          `Process #${assetTask.process_id}`
                        : "-"}
                      </div>

                      <div className="cell">
                        <span className="badge">{assetTask.type}</span>
                      </div>

                      <div className="cell">
                        <span
                          className={
                            assetTask.input || assetTask.output ?
                              "badge success"
                            : "badge"
                          }
                        >
                          {assetTask.input ?
                            "Input"
                          : assetTask.output ?
                            "Output"
                          : "-"}
                        </span>
                      </div>

                      <div className="cell actions">
                        <div
                          className="actions-menu-wrapper"
                          style={{ position: "relative", display: "inline-block" }}
                        >
                          <button
                            className="action-menu-btn"
                            aria-haspopup="true"
                            aria-expanded={activeDropdown === assetTask.asset_task_id}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (activeDropdown === assetTask.asset_task_id) {
                                setActiveDropdown(null);
                              } else {
                                setDropdownDirection(getActionDropdownDirection(e.currentTarget));
                                setActiveDropdown(assetTask.asset_task_id);
                              }
                            }}
                          >
                            Actions ▼
                          </button>
                          {activeDropdown === assetTask.asset_task_id && (
                            <div
                              className={`action-dropdown-menu${
                                dropdownDirection === "up" ? " dropdown-up" : ""
                              }`}
                              role="menu"
                            >
                              <button
                                className="action-menu-item"
                                role="menuitem"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveDropdown(null);
                                  handleView(assetTask);
                                }}
                              >
                                View
                              </button>
                              <button
                                className="action-menu-item"
                                role="menuitem"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveDropdown(null);
                                  handleEdit(assetTask);
                                }}
                              >
                                Edit
                              </button>
                              <button
                                className="action-menu-item danger"
                                role="menuitem"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveDropdown(null);
                                  removeSingle(assetTask);
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                }
              </div>
            </>
          }
        </div>
      </div>

      <Modal
        open={modal.open}
        title={modal.title}
        onCancel={closeModal}
        cancelText="Close"
      >
        {modal.message}
      </Modal>

      <ConfirmModal
        open={confirmDelete.open}
        title={confirmDelete.isBulk ? "Confirm Bulk Delete" : "Confirm Delete"}
        message={
          confirmDelete.isBulk ?
            `Are you sure you want to delete ${confirmDelete.count} ${
              confirmDelete.count === 1 ? "item" : "items"
            }? This action cannot be undone.`
          : `Are you sure you want to delete "${confirmDelete.description}"? This action cannot be undone.`
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={
          confirmDelete.isBulk ? confirmBulkDelete : confirmRemoveSingle
        }
        onCancel={() =>
          setConfirmDelete({
            open: false,
            id: null,
            description: "",
            isBulk: false,
            count: 0,
          })
        }
        isDanger={true}
      />
    </>
  );
};

export default AssetTaskManagementPage;

